<template>
  <div v-if="isMe">
    <OButton
      pure
      :href="url"
      class="u-center h-10 rounded-full bg-[#424242] px-6 text-white text-md font-semibold"
      @click="mailConfirmation"
    >
      {{ $t('profile.edit') }}
    </OButton>
  </div>
  <div v-else class="flex items-center">
    <ProfilePopperMenu v-if="!blocked && !blockedMe" class="relative mr-2">
      <OButton
        pure
        class="w-full flex items-center text-primary-100 text-sm font-medium hover:bg-alpha-5 px-4 py-2"
        @click="handleSendMessage"
      >
        <OIcon icon="envelope" svg-class="w-6 h-6 sm:w-[18px] sm:h-[18px] mr-2.5" />
        {{ $t('comment-item.send-message') }}
      </OButton>
      <OButton pure class="w-full flex items-center text-primary-100 text-sm font-medium hover:bg-alpha-5 px-4 py-2" @click="handleBlock">
        <OIcon icon="block" svg-class="w-6 h-6 sm:w-[18px] sm:h-[18px] mr-2.5" />
        {{ blocked ? $t('profile.unblock') : $t('profile.block') }}
      </OButton>
    </ProfilePopperMenu>
    <!-- user follow action -->
    <OButton
      v-if="!blocked && !blockedMe"
      pure
      class="u-center h-10 rounded-full px-6 text-md font-semibold"
      :class="{ 'border border-divider-[#E2E5ED]': state.user.followStatus, 'bg-o-blue text-white': !state.user.followStatus }"
      @click="handleFollowUser"
    >
      {{ followed ? $t('profile.following') : $t('profile.follow') }}
    </OButton>
    <!-- unblock user -->
    <OButton v-if="blocked" pure class="u-center h-10 rounded-full px-6 text-white text-md font-semibold bg-o-red" @click="handleBlock">
      {{ $t('profile.unblock') }}
    </OButton>
  </div>
</template>

<script>
import { defineComponent, useRoute, computed, useContext } from '@nuxtjs/composition-api';
import { useDevice } from '@/composables/useDevice';
import { useProfile } from '@/modules/profile/state/useProfile';
import ProfilePopperMenu from '@/modules/profile/components/ProfileView/ProfilePopperMenu.vue';
import { useAuth } from '~/modules/auth/state/useAuth';
import { useModal, useToast, useDialog } from '~/composables/useModal';
import { OButton, OIcon } from '~/modules/common/components/ui';
import { MESSAGE_PATH, PROFILE_PATH, SETTINGS_PATH } from '~/constants/config';
import Favoriler from '~/pages/profil/_username/index/favoriler.vue';
import Icerikler from '~/pages/profil/_username/index/icerikler.vue';
import { eventBus } from '~/modules/common/utils/eventBus';

export default defineComponent({
  components: { Favoriler, Icerikler, ProfilePopperMenu, OButton, OIcon },
  setup() {
    const { isMobile } = useDevice();
    const { state, setToggleUserFollow, setToggleUserBlock } = useProfile();
    const { user, isLoggedIn } = useAuth();
    const route = useRoute();
    const { openLoginModal, openModal } = useModal();
    const { openDialog } = useDialog();
    const { toastSuccess, toastDanger } = useToast();
    const { $service, i18n, redirect } = useContext();

    const username = route.value.params.username;
    const id = computed(() => state.value.user?._id);
    const isMe = user.value?.username === username;
    const blocked = computed(() => state.value.user?.blockStatus);
    const blockedMe = computed(() => state.value.user?.blocked);
    const followed = computed(() => state.value.user?.followStatus);

    const url = isLoggedIn.value && !user.value?.emailVerified ? null : `/${PROFILE_PATH}/${username}/${SETTINGS_PATH}`;

    const mailConfirmation = () => {
      if (!user.value?.emailVerified) {
        openModal(import('~/modules/common/components/modals/CommentEmailVerify' /* webpackChunkName: "CommentEmailVerify" */), {
          type: 'edit',
        });
      }
    };

    const handleFollowUser = async () => {
      if (!isLoggedIn.value) {
        openLoginModal();
        return;
      }
      try {
        if (state.value.user.followStatus) {
          await $service.user.unFollowUser(id.value);
        } else {
          await $service.user.followUser(id.value);
        }
        setToggleUserFollow(user);
      } catch (error) {
        toastDanger(i18n.t('profile.failed-operation'));
      }
    };

    const handleSendMessage = () => {
      if (!isLoggedIn.value) {
        openLoginModal();
        return;
      }
      if (isMobile.value) {
        redirect(`/${MESSAGE_PATH}/${state.value.user.username}/${state.value.user._id}`);
      }
      eventBus.emit('open-conversation', { user: state.value.user });
    };

    const handleBlock = async () => {
      try {
        if (!isLoggedIn.value) {
          openLoginModal();
          return;
        }
        if (state.value.user.blockStatus) {
          await $service.user.unBlockUser(id.value);
          toastSuccess(i18n.t('profile.unblock-message'));
          setToggleUserBlock(user);
        } else {
          openDialog({
            description: i18n.t('profile.block-message'),
            actions: [
              { text: i18n.t('main.dialog.cancel') },
              {
                text: i18n.t('profile.block'),
                type: 'danger',
                action: async (closeDialog) => {
                  try {
                    await $service.user.blockUser(id.value);
                    setToggleUserBlock(user);
                    toastDanger(i18n.t('profile.blocked-message'));
                  } catch (error) {
                    toastDanger(i18n.t('profile.failed-operation'));
                  } finally {
                    closeDialog();
                  }
                },
              },
            ],
          });
        }
      } catch (error) {
        toastDanger(i18n.t('profile.failed-operation'));
      }
    };

    return {
      isMobile,
      state,
      username,
      isMe,
      blocked,
      blockedMe,
      followed,
      url,
      handleFollowUser,
      handleSendMessage,
      handleBlock,
      mailConfirmation,
    };
  },
});
</script>
